import type { FormulaRegion, OutputType } from "./types";
import { addressFromRC } from "../rules/audit/addressing";
import { classifyFormula } from "./classify";
import { loadKindRules, regionShape } from "./kindRules";
import { getWorkbookModel, Origin, ValueKind } from "../model";
import type { ValueKindValue } from "../model";

// Date number-format detection. Strips text literals, escaped chars, and
// bracketed clauses, then looks for y / d / mmm+ tokens.
const DATE_TOKEN_RE = /[yd]|m{3,}/i;
function isDateFormat(fmt: string | undefined): boolean {
  if (!fmt || fmt === "General") return false;
  const stripped = fmt
    .replace(/"[^"]*"/g, "")
    .replace(/\\./g, "")
    .replace(/\[[^\]]*\]/g, "");
  return DATE_TOKEN_RE.test(stripped);
}

/**
 * Scan every worksheet's used range, group cells by R1C1 formula, and find
 * 4-connected rectangular regions for each unique formula. Cell grouping
 * uses the substrate's `r1c1Ids` (integer comparison, not string-keyed
 * Map); per-sheet Office.js loads collapse into a single getWorkbookModel
 * call shared with audit / strings / info / data-islands / cell-types.
 *
 * The strict isDateFormat predicate (y/d/mmm+) is re-applied per cell
 * rather than relying on the sink's broader date heuristic — the latter
 * would shift output-type tallies. Tally helper translates ValueKind +
 * numericGrid + textValueIds + numFmtIds into the same TypeCounts shape
 * the resolveOutputType helper expects.
 */
export async function scanFormulaRegions(): Promise<FormulaRegion[]> {
  const userKindRules = loadKindRules();
  const model = await getWorkbookModel("workbook");
  const r1c1Strings = model.catalogs.r1c1Strings;
  const numFmtStrings = model.catalogs.numFmtStrings;
  const textValueStrings = model.catalogs.textValueStrings;

  const results: FormulaRegion[] = [];

  for (const sheet of Array.from(model.sheets.values())) {
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const r1c1Ids = sheet.r1c1Ids;
    const cellOrigin = sheet.cellOrigin;
    const cellValueKind = sheet.cellValueKind;
    const numericGrid = sheet.numericGrid;
    const textValueIds = sheet.textValueIds;
    const numFmtIds = sheet.numFmtIds;

    // Group flat indices by formula pattern id
    const byPatternId = new Map<number, number[]>();
    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let cc = 0; cc < cols; cc++) {
        const idx = rowBase + cc;
        if (cellOrigin[idx] !== Origin.FORMULA) continue;
        const id = r1c1Ids[idx];
        if (id < 0) continue;
        const arr = byPatternId.get(id);
        if (arr) arr.push(idx);
        else byPatternId.set(id, [idx]);
      }
    }

    for (const [patternId, indices] of Array.from(byPatternId)) {
      const formula = r1c1Strings[patternId];
      const cellSet = new Set(indices);
      const visited = new Set<number>();

      for (const startIdx of indices) {
        if (visited.has(startIdx)) continue;
        visited.add(startIdx);

        const queue: number[] = [startIdx];
        let head = 0;
        let minR = Math.floor(startIdx / cols);
        let maxR = minR;
        let minC = startIdx % cols;
        let maxC = minC;
        let count = 0;
        const typeCounts: TypeCounts = {
          boolean: 0,
          date: 0,
          flag: 0,
          number: 0,
          text: 0,
          error: 0,
          empty: 0,
        };

        while (head < queue.length) {
          const k = queue[head++];
          const r = Math.floor(k / cols);
          const cc = k % cols;
          count++;
          if (r < minR) minR = r;
          if (r > maxR) maxR = r;
          if (cc < minC) minC = cc;
          if (cc > maxC) maxC = cc;

          const vk = cellValueKind[k] as ValueKindValue;
          const fmtId = numFmtIds[k];
          const fmt = fmtId >= 0 ? numFmtStrings[fmtId] : undefined;
          tallyCell(typeCounts, vk, numericGrid[k], textValueIds[k], textValueStrings, fmt);

          // 4-connected neighbors; only follow into cells in the same
          // pattern's set.
          if (r > 0) {
            const nk = (r - 1) * cols + cc;
            if (cellSet.has(nk) && !visited.has(nk)) {
              visited.add(nk);
              queue.push(nk);
            }
          }
          if (r + 1 < rows) {
            const nk = (r + 1) * cols + cc;
            if (cellSet.has(nk) && !visited.has(nk)) {
              visited.add(nk);
              queue.push(nk);
            }
          }
          if (cc > 0) {
            const nk = r * cols + (cc - 1);
            if (cellSet.has(nk) && !visited.has(nk)) {
              visited.add(nk);
              queue.push(nk);
            }
          }
          if (cc + 1 < cols) {
            const nk = r * cols + (cc + 1);
            if (cellSet.has(nk) && !visited.has(nk)) {
              visited.add(nk);
              queue.push(nk);
            }
          }
        }

        const absMinR = startRow + minR;
        const absMinC = startCol + minC;
        const absMaxR = startRow + maxR;
        const absMaxC = startCol + maxC;
        const bboxCells = (maxR - minR + 1) * (maxC - minC + 1);
        const address =
          absMinR === absMaxR && absMinC === absMaxC
            ? addressFromRC(absMinR, absMinC)
            : `${addressFromRC(absMinR, absMinC)}:${addressFromRC(absMaxR, absMaxC)}`;

        // Count truly-empty cells inside the bounding box
        let blankCount = 0;
        for (let rr = minR; rr <= maxR; rr++) {
          const rowBase = rr * cols;
          for (let cc2 = minC; cc2 <= maxC; cc2++) {
            if (cellOrigin[rowBase + cc2] === Origin.EMPTY) blankCount++;
          }
        }
        const blankPercent = bboxCells > 0 ? (blankCount / bboxCells) * 100 : 0;
        const samePercent = bboxCells > 0 ? (count / bboxCells) * 100 : 0;

        results.push({
          formula,
          sheetName: sheet.name,
          minRow: absMinR,
          minCol: absMinC,
          maxRow: absMaxR,
          maxCol: absMaxC,
          cellCount: count,
          address,
          isFullRectangle: count === bboxCells,
          blankCount,
          blankPercent,
          samePercent,
          hasExternalLinks: detectExternalLinks(formula),
          hasHardcodedValues: detectHardcodedValues(formula),
          kind: classifyFormula(
            formula,
            regionShape(absMinR, absMinC, absMaxR, absMaxC),
            userKindRules
          ),
          outputType: resolveOutputType(typeCounts),
        });
      }
    }
  }

  results.sort(
    (a, b) => a.sheetName.localeCompare(b.sheetName) || a.minRow - b.minRow || a.minCol - b.minCol
  );

  return results;
}

/**
 * Tally one cell into the typeCounts buckets, reading from the model's
 * Layer 0 arrays. Behavior mirrors the legacy tallyCell for parity.
 */
function tallyCell(
  counts: TypeCounts,
  vk: ValueKindValue,
  numericValue: number,
  textValueId: number,
  textValueStrings: string[],
  fmt: string | undefined
): void {
  switch (vk) {
    case ValueKind.BOOLEAN:
      counts.boolean++;
      return;
    case ValueKind.ERROR:
      counts.error++;
      return;
    case ValueKind.TEXT: {
      const s = textValueId >= 0 ? textValueStrings[textValueId] : "";
      if (s.trim().length === 0) counts.empty++;
      else counts.text++;
      return;
    }
    case ValueKind.NUMBER:
    case ValueKind.DATE:
      if (isDateFormat(fmt)) {
        counts.date++;
      } else if (numericValue === 0 || numericValue === 1) {
        counts.flag++;
      } else {
        counts.number++;
      }
      return;
    case ValueKind.NONE:
    default:
      counts.empty++;
  }
}

interface TypeCounts {
  boolean: number;
  date: number;
  flag: number;
  number: number;
  text: number;
  error: number;
  empty: number;
}

/**
 * Boil a region's per-cell type counts down to a single OutputType.
 *
 * Rules (in priority order):
 *   - Region is purely one category → that category
 *   - Region is all-numeric (no boolean / text / error among non-empty)
 *     → consolidate: dates win if they're a majority of valid cells;
 *       otherwise it's "number" (flag is treated as a subset of number
 *       unless ALL cells are 0/1)
 *   - Everything else → "mixed"
 *   - All empty / no valid cells → "unknown"
 */
function resolveOutputType(counts: TypeCounts): OutputType {
  const valid = counts.boolean + counts.date + counts.flag + counts.number + counts.text;
  if (valid === 0) {
    if (counts.error > 0) return "error";
    return "unknown";
  }

  // Pure single category
  if (counts.boolean === valid) return "boolean";
  if (counts.text === valid) return "text";
  if (counts.date === valid) return "date";
  if (counts.flag === valid) return "flag";
  if (counts.number === valid) return "number";

  // All-numeric (no boolean, no text)
  const numericTotal = counts.date + counts.flag + counts.number;
  if (counts.boolean === 0 && counts.text === 0 && numericTotal === valid) {
    // Date wins if it's at least half
    if (counts.date * 2 >= valid) return "date";
    // Otherwise treat flag as a subset of number when mixed with regular numbers
    return "number";
  }

  return "mixed";
}

/**
 * Detect references to external workbooks. External-link refs look like
 * `'[Book1.xlsx]Sheet1'!R1C1` or `[Book1.xlsx]Sheet1!R1C1` — always have a
 * bracketed token containing letters or a period. R1C1 relative offsets
 * (`[-5]`, `[3]`) contain only digits and signs, so they don't match.
 */
function detectExternalLinks(formula: string): boolean {
  // Strip string literals so bracketed text inside strings can't false-positive.
  const stripped = formula.replace(/"(?:[^"]|"")*"/g, "");
  return /\[[^\]]*[a-zA-Z.][^\]]*\]/.test(stripped);
}

/**
 * Detect hardcoded numeric literals in an R1C1 formula. Walks character by
 * character, skipping string literals, quoted sheet names, function/identifier
 * names, and R/C reference patterns (`R5`, `C7`, `R[-3]`, `C[2]`, `RC`,
 * `R5C7`, etc.). Any digit found OUTSIDE those contexts is a literal.
 *
 * Examples:
 *   `=R[-9]C:RC`         → false  (pure references)
 *   `=RC[-1]*0.07`        → true   (`0.07` literal)
 *   `=COUNT(R1C1:R10C10)` → false  (digits are absolute row/col)
 *   `=INDEX(R1C1:R5C5,3)` → true   (`3` literal arg)
 */
function detectHardcodedValues(formula: string): boolean {
  const f = formula.startsWith("=") ? formula.slice(1) : formula;
  const len = f.length;
  let i = 0;

  while (i < len) {
    const c = f[i];

    // String literal
    if (c === '"') {
      i++;
      while (i < len) {
        if (f[i] === '"') {
          if (f[i + 1] === '"') {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      continue;
    }
    // Quoted sheet name
    if (c === "'") {
      i++;
      while (i < len) {
        if (f[i] === "'") {
          if (f[i + 1] === "'") {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      continue;
    }

    // R or C — either a reference or the start of a function name.
    if (c === "R" || c === "r" || c === "C" || c === "c") {
      const next = i + 1 < len ? f[i + 1] : "";
      // Function name pattern: R/C followed by another letter (and not the
      // standalone 'RC' / 'CR' pair). For "RC", the R is followed by 'C';
      // we treat R as reference (handled below) and let C be consumed next.
      const isRcPair = (c === "R" || c === "r") && (next === "C" || next === "c");
      if (/[A-Za-z]/.test(next) && !isRcPair) {
        // Identifier — skip the whole token
        while (i < len && /[A-Za-z0-9._]/.test(f[i])) i++;
        continue;
      }
      // Reference — skip R/C + optional offset/abs
      i++;
      if (i < len && f[i] === "[") {
        while (i < len && f[i] !== "]") i++;
        if (i < len) i++; // past ]
      } else if (i < len && /\d/.test(f[i])) {
        while (i < len && /\d/.test(f[i])) i++;
      }
      continue;
    }

    // Any other letter starts an identifier (function name, defined name, etc.)
    if (/[A-Za-z_]/.test(c)) {
      while (i < len && /[A-Za-z0-9._]/.test(f[i])) i++;
      continue;
    }

    // Bracket span outside R/C — structured table refs (Table1[Column 1])
    // or external workbook refs ([Book1.xlsx]). Skip the whole content; the
    // R/C branch already consumed `[N]` offset spans inline before reaching
    // here, so any remaining `[` is non-numeric content.
    if (c === "[") {
      while (i < len && f[i] !== "]") i++;
      if (i < len) i++; // past ]
      continue;
    }

    // Digit not preceded by R/C and not inside a string → hardcoded literal.
    if (/\d/.test(c)) return true;

    i++;
  }
  return false;
}
